<template>
	<div id="app" class="gw-page gw-legal">
		<HeadNav />
		<main class="gw-shell">
			<article class="gw-article">
				<header class="gw-page-header">
					<p class="gw-eyebrow">{{ page.eyebrow }}</p>
					<h1>{{ page.title }}</h1>
					<p class="gw-lead">{{ page.intro }}</p>
				</header>

				<div class="gw-content">
					<section v-for="section in page.sections" :key="section.title" class="gw-section">
						<h2>{{ section.title }}</h2>
						<p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
						<ul v-if="section.items">
							<li v-for="item in section.items" :key="item">{{ item }}</li>
						</ul>
					</section>

					<section v-if="pageKey === 'contact-us'" class="gw-section">
						<h2>Email</h2>
						<div class="gw-email-box">
							<a href="mailto:support@gb-whatsapp.cc">support@gb-whatsapp.cc</a>
						</div>
					</section>
				</div>
			</article>
		</main>
	</div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import '@/css/gb-whatsapp.scss';
import HeadNav from '@/components/HeadNav.vue';

const pages = {
	'privacy-policy': {
		eyebrow: 'Legal information',
		title: 'Privacy Policy',
		intro:
			'This policy explains how gb-whatsapp.cc handles information when you visit this independent information website or contact us by email.',
		sections: [
			{
				title: 'Our Role',
				paragraphs: [
					'gb-whatsapp.cc publishes information about the third-party application commonly called GB WhatsApp. We do not develop, maintain, distribute, or officially support GB WhatsApp. We are not affiliated with WhatsApp LLC, Meta Platforms, Inc., or any related company.',
				],
			},
			{
				title: 'Information We May Collect',
				paragraphs: [
					'We may receive basic technical information that is normally processed when a website is visited, such as browser type, device type, approximate location derived from network information, referring pages, and pages viewed.',
					'If you email us, we may receive your email address and the contents of your message so that we can respond.',
				],
			},
			{
				title: 'How Information Is Used',
				paragraphs: ['Information may be used to operate the website, improve our guides, understand general traffic patterns, prevent abuse, and answer questions sent to our support email.'],
			},
			{
				title: 'Cookies and Similar Technologies',
				paragraphs: [
					'The site or services used by the site may use cookies or similar technologies for basic functionality, analytics, and security. You can restrict or disable cookies through your browser settings, although some site features may then work differently.',
				],
			},
			{
				title: 'Third-Party Services and Links',
				paragraphs: [
					'This website may link to third-party pages or use third-party services. Their privacy practices are controlled by their own policies. We are not responsible for the content, security, availability, or data practices of third-party websites.',
				],
			},
			{
				title: 'Data Security and Retention',
				paragraphs: [
					'We take reasonable steps to protect information under our control. No website, email system, or method of transmission is completely secure, so absolute security cannot be guaranteed. Information is retained only for as long as needed for the purpose for which it was received or as required by law.',
				],
			},
			{
				title: 'Changes to This Policy',
				paragraphs: ['We may update this policy when the website or applicable requirements change. The revised version will be posted on this page.'],
			},
		],
	},
	disclaimer: {
		eyebrow: 'Important notice',
		title: 'Disclaimer',
		intro:
			'gb-whatsapp.cc is an independent informational website. Please review this notice before relying on any guide or choosing to install third-party software.',
		sections: [
			{
				title: 'No Affiliation',
				paragraphs: [
					'gb-whatsapp.cc is not affiliated with, endorsed by, sponsored by, or officially connected to WhatsApp LLC, Meta Platforms, Inc., or any of their subsidiaries.',
					'GB WhatsApp is a third-party modified application. We do not develop, own, maintain, control, or distribute the application.',
				],
			},
			{
				title: 'Informational Purpose Only',
				paragraphs: [
					'The content on this website is provided for general information. Features, versions, compatibility, availability, and installation requirements may change without notice because third-party builds are outside our control.',
				],
			},
			{
				title: 'Risks of Unofficial Software',
				paragraphs: [
					'WhatsApp does not support unofficial WhatsApp applications and warns that using them may create privacy, security, account-access, or account-restriction risks. These risks may include temporary or permanent account limitations, malware exposure, data loss, and failed chat transfers.',
					'An "Anti-Ban" label is not a guarantee. No statement on this website should be understood as a promise that an account will remain unrestricted.',
				],
			},
			{
				title: 'Your Responsibility',
				paragraphs: [
					'You are responsible for deciding whether to download or use any third-party application and for complying with applicable laws and service terms. Use third-party software at your own discretion.',
				],
			},
		],
	},
	'about-us': {
		eyebrow: 'About this website',
		title: 'About Us',
		intro:
			'gb-whatsapp.cc is an independent information resource built to explain GB WhatsApp clearly, including its features, limitations, and risks.',
		sections: [
			{
				title: 'What We Publish',
				paragraphs: [
					'We publish guides and explanatory articles about third-party WhatsApp modifications. Our goal is to help readers understand the difference between official WhatsApp and unofficial clients before making a decision.',
					'Content may cover features, privacy settings, compatibility, installation steps, account restrictions, and commonly asked questions.',
				],
			},
			{
				title: 'What We Do Not Do',
				paragraphs: [
					'We do not develop, own, maintain, control, or officially support GB WhatsApp. We are not the app developer and we are not affiliated with WhatsApp or Meta.',
					'We also do not provide official WhatsApp support or guarantee the behavior, security, privacy, or availability of third-party software.',
				],
			},
			{
				title: 'Editorial Approach',
				paragraphs: [
					'Our guides aim to be direct about uncertainty. Features may vary between builds, and unofficial applications are not covered by WhatsApp\'s official support or security framework. When official WhatsApp is the safer choice for an account, we say so.',
				],
			},
		],
	},
	'contact-us': {
		eyebrow: 'Get in touch',
		title: 'Contact Us',
		intro:
			'Use the email address below for questions, corrections, or feedback about content published on gb-whatsapp.cc.',
		sections: [
			{
				title: 'Before You Contact Us',
				paragraphs: [
					'We are an independent information website, not the developer of GB WhatsApp and not affiliated with WhatsApp or Meta. We cannot provide account recovery, ban removal, official technical support, or APK troubleshooting.',
				],
			},
		],
	},
};

export default {
	name: 'CompliancePage',
	components: {
		HeadNav,
	},
	props: {
		pageKey: {
			type: String,
			required: true,
		},
	},
	computed: {
		page() {
			return pages[this.pageKey];
		},
	},
};
</script>
